import React from 'react';
import { AlertTriangle, ArrowRight, BookOpen, CalendarDays, CheckCircle2, ClipboardCheck, ListChecks, Target } from 'lucide-react';
import { getClassOperationalRows } from '../../utils/teacherClasses';

const getCourseDetail = (course = {}) => (
    course.currentUnit ||
    course.current_unit ||
    course.unit ||
    course.status ||
    'Course context loaded from teacher profile.'
);

const getBestNextMove = (course = {}) => (
    course.bestNextMove ||
    course.best_next_move ||
    course.nextActions?.[0] ||
    course.next_actions?.[0] ||
    course.suggestedSaigeAction ||
    'Choose the next teacher-facing planning move.'
);

const getPrepChecklist = (course = {}) => {
    const checklist = course.teacherPrepChecklist || course.teacher_prep_checklist || course.nextActions || course.next_actions || [];
    return Array.isArray(checklist) ? checklist.filter(Boolean).slice(0, 3) : [];
};

const getGradeCadence = (course = {}) => {
    const cadence = course.gradeCadence || course.grade_cadence;
    if (!cadence) return null;

    const minorCompleted = Number(cadence.minorCompleted ?? cadence.minor_completed ?? 0);
    const minorRequired = Number(cadence.minorRequired ?? cadence.minor_required ?? 0);
    const majorCompleted = Number(cadence.majorCompleted ?? cadence.major_completed ?? 0);
    const majorRequired = Number(cadence.majorRequired ?? cadence.major_required ?? 0);

    return {
        label: cadence.windowLabel || cadence.window_label || 'Current grade window',
        minorText: `${minorCompleted}/${minorRequired} minor`,
        majorText: `${majorCompleted}/${majorRequired} major`,
        needsAttention: minorCompleted < minorRequired || majorCompleted < majorRequired,
    };
};

const flattenEvents = (eventsByDay = {}) => (
    Object.entries(eventsByDay || {}).flatMap(([dateLabel, items]) => (
        Array.isArray(items) ? items.map((item) => ({ ...item, dateLabel })) : []
    ))
);

const getCourseEvents = (operatingState = {}, course = {}) => {
    const courseName = (course.name || '').toLowerCase();
    if (!courseName) return [];

    return [
        ...flattenEvents(operatingState.calendarEvents),
        ...flattenEvents(operatingState.fallbackCalendarEvents),
    ].filter((event) => (
        (event.course || event.subject || '').toLowerCase() === courseName
    )).slice(0, 2);
};

const TeacherClassesWorkspace = ({ operatingState, onGenerateLesson, onOpenGradingStudio }) => {
    const courses = operatingState?.courses || [];
    const classRows = getClassOperationalRows(courses);
    const highAttentionCourses = courses.filter((course) => course.urgency === 'high').length;
    const prepQueue = (operatingState?.aiPreparationQueue || []).slice(0, 3);

    return (
        <div className="space-y-5">
            <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    Classes
                </p>
                <h1 className="mt-2 text-3xl font-semibold leading-tight text-slate-950 sm:text-4xl dark:text-white">
                    Active course workspace
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                    Course-level planning and grading entry points using teacher-owned operating state.
                </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Active courses</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{courses.length}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Loaded from the authenticated teacher profile and safe fallback context.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Needs attention</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{highAttentionCourses}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Pacing, safety, or grading needs to check before class starts.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Prep queue</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{prepQueue.length}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Teacher-facing packets, grade checks, and lesson moves ready to generate.
                    </p>
                </div>
            </div>

            {prepQueue.length > 0 && (
                <section className="rounded-2xl border border-white/80 bg-white/90 p-5 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <div className="mb-4 flex items-center gap-2">
                        <ListChecks size={17} className="text-amber-700 dark:text-amber-300" />
                        <h2 className="text-sm font-semibold text-slate-950 dark:text-white">Today&apos;s Saige prep queue</h2>
                    </div>
                    <div className="grid gap-3 lg:grid-cols-3">
                        {prepQueue.map((item) => (
                            <div key={item.id || item.label} className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{item.label}</p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.output || item.reason}</p>
                                <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                    {item.course || 'All courses'}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {classRows.length > 0 && (
                <section className="rounded-2xl border border-white/80 bg-white/90 p-5 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-sm font-semibold text-slate-950 dark:text-white">Operational class list</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Period, pacing, grade-window, and next-move signals for today&apos;s teacher workflow.
                            </p>
                        </div>
                    </div>
                    <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-100 dark:divide-slate-700 dark:border-slate-700">
                        {classRows.map((row) => (
                            <div key={row.id} className="grid gap-3 bg-white p-4 dark:bg-slate-900/35 lg:grid-cols-[1.1fr_0.9fr_1.4fr_auto] lg:items-center">
                                <div>
                                    <p className="text-sm font-semibold text-slate-950 dark:text-white">{row.name}</p>
                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{row.scheduleLabel}</p>
                                </div>
                                <div>
                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                        row.status.tone === 'urgent'
                                            ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200'
                                            : row.status.tone === 'attention'
                                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200'
                                                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200'
                                    }`}>
                                        {row.status.label}
                                    </span>
                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{row.cadence.minorText} / {row.cadence.majorText}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Next move</p>
                                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{row.nextMove}</p>
                                </div>
                                <div className="flex gap-2 lg:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => onGenerateLesson(row.raw)}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFC107] text-[#1d1d1f] transition-colors hover:bg-[#e6ad00]"
                                        aria-label={`Generate lesson for ${row.name}`}
                                    >
                                        <BookOpen size={16} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onOpenGradingStudio}
                                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-slate-500"
                                        aria-label={`Open grade workflow for ${row.name}`}
                                    >
                                        <ClipboardCheck size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {courses.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 p-8 text-center dark:border-slate-700 dark:bg-slate-900/55">
                    <BookOpen size={32} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        No active courses are available yet.
                    </p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Update teacher profile courses in Settings to populate this workspace.
                    </p>
                </div>
            ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                    {courses.map((course) => {
                        const cadence = getGradeCadence(course);
                        const prepChecklist = getPrepChecklist(course);
                        const courseEvents = getCourseEvents(operatingState, course);
                        return (
                            <article
                                key={course.id}
                                className="rounded-2xl border border-white/80 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] dark:border-slate-700/60 dark:bg-[#171d26]/95"
                            >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
                                        {course.name}
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                                        {getCourseDetail(course)}
                                    </p>
                                </div>
                                <span className="rounded-full border border-black/10 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900/35 dark:text-slate-200">
                                    {course.status || course.urgency || 'Active'}
                                </span>
                            </div>

                            <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    <Target size={14} />
                                    Best next move
                                </div>
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                    {getBestNextMove(course)}
                                </p>
                            </div>

                            {(course.projectCheckpoint || cadence) && (
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    {course.projectCheckpoint && (
                                        <div className="rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/40">
                                            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                <CheckCircle2 size={14} />
                                                Next checkpoint
                                            </div>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                {course.projectCheckpoint.studentDeliverable || course.projectCheckpoint.label}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                {course.projectCheckpoint.gradeOpportunity || course.projectCheckpoint.teacherPrep}
                                            </p>
                                        </div>
                                    )}
                                    {cadence && (
                                        <div className="rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/40">
                                            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                {cadence.needsAttention ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
                                                Grade cadence
                                            </div>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{cadence.label}</p>
                                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                                {cadence.minorText} / {cadence.majorText}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(prepChecklist.length > 0 || courseEvents.length > 0) && (
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    {prepChecklist.length > 0 && (
                                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                                            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                <ListChecks size={14} />
                                                Before class
                                            </div>
                                            <ul className="space-y-1.5">
                                                {prepChecklist.map((item) => (
                                                    <li key={item} className="text-xs text-slate-600 dark:text-slate-300">{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {courseEvents.length > 0 && (
                                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                                            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                <CalendarDays size={14} />
                                                Upcoming
                                            </div>
                                            <div className="space-y-2">
                                                {courseEvents.map((event) => (
                                                    <div key={`${event.dateLabel}-${event.time || event.start_time}-${event.topic || event.lesson_title}`}>
                                                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                                                            {event.topic || event.lesson_title || event.type || event.category}
                                                        </p>
                                                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                                            {event.time || event.start_time || event.dateLabel}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-5 grid gap-2 sm:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => onGenerateLesson(course)}
                                    className="flex items-center justify-center gap-2 rounded-xl bg-[#FFC107] px-4 py-3 text-sm font-semibold text-[#1d1d1f] transition-colors hover:bg-[#e6ad00]"
                                >
                                    <BookOpen size={16} />
                                    Generate Lesson
                                    <ArrowRight size={15} />
                                </button>
                                <button
                                    type="button"
                                    onClick={onOpenGradingStudio}
                                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-slate-500"
                                >
                                    <ClipboardCheck size={16} />
                                    Open Grade Workflow
                                </button>
                            </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TeacherClassesWorkspace;
